/* ===================================================
   Miaamor Shopify Theme — theme.js
   Vanilla JS, no framework. Handles:
   - Mobile nav drawer
   - Search drawer + predictive search
   - Locale menu toggle
   - Wishlist (localStorage shim)
   - Quantity inputs (product page + cart)
   - AJAX add-to-cart from product cards
   - Cart line updates (quantity, remove)
   - Free shipping progress bar
   - Product page variant + media swap
   - Async product recommendations
   - Customer Privacy cookie banner
   =================================================== */

(function () {
  'use strict';

  const win = window;
  const doc = document;
  const theme = win.theme || {};
  const moneyFormat = theme.moneyFormat || '€{{amount}}';

  // ---------------------------- helpers ----------------------------
  function $(sel, root) { return (root || doc).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || doc).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({ headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }, opts || {}))
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
  }
  function debounce(fn, wait) {
    let t; return function () { const ctx = this, args = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(ctx, args); }, wait); };
  }
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    const euros = cents / 100;
    const fmt = moneyFormat || '€{{amount}}';
    // Handle all Shopify money format tokens — check most-specific first to avoid substring conflicts
    if (/\{\{amount_with_comma_separator_no_decimals\}\}/.test(fmt)) {
      const n = Math.round(euros).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return fmt.replace('{{amount_with_comma_separator_no_decimals}}', n);
    }
    if (/\{\{amount_with_comma_separator\}\}/.test(fmt)) {
      const n = euros.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return fmt.replace('{{amount_with_comma_separator}}', n);
    }
    if (/\{\{amount_no_decimals\}\}/.test(fmt)) {
      return fmt.replace('{{amount_no_decimals}}', Math.round(euros).toString());
    }
    if (/\{\{amount_with_apostrophe_separator\}\}/.test(fmt)) {
      const n = euros.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return fmt.replace('{{amount_with_apostrophe_separator}}', n);
    }
    return fmt.replace(/\{\{\s*amount\s*\}\}/, euros.toFixed(2).replace(/\.00$/, ''));
  }
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function safeRun(name, fn) {
    try { fn(); } catch (e) { if (win.console && console.warn) console.warn('[theme] init failed:', name, e); }
  }

  // ---------------------------- mobile menu drawer ----------------------------
  function initMobileNav() {
    var menu = $('[data-mobile-menu]');
    if (!menu) return;

    // Portal: move to document.body so position:fixed escapes the sticky
    // header's backdrop-filter containing block (iOS Safari / mobile Chrome fix).
    if (menu.parentElement !== doc.body) doc.body.appendChild(menu);

    var openBtns = $$('[data-mobile-menu-open]');
    var closeBtns = $$('[data-mobile-menu-close], [data-mobile-menu-backdrop]');
    var links = $$('a', menu);

    function openMenu() {
      doc.body.classList.add('mobile-menu-open');
      doc.documentElement.style.overflow = 'hidden';
      doc.body.style.overflow = 'hidden';
      menu.setAttribute('aria-hidden', 'false');
      openBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
    }
    function closeMenu() {
      doc.body.classList.remove('mobile-menu-open');
      doc.documentElement.style.overflow = '';
      doc.body.style.overflow = '';
      menu.setAttribute('aria-hidden', 'true');
      openBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
    }

    openBtns.forEach(function (btn) { on(btn, 'click', openMenu); });
    closeBtns.forEach(function (btn) { on(btn, 'click', closeMenu); });
    on(doc, 'keydown', function (e) {
      if (e.key === 'Escape' && doc.body.classList.contains('mobile-menu-open')) closeMenu();
    });
    links.forEach(function (link) { on(link, 'click', closeMenu); });
  }

  // ---------------------------- search drawer + predictive ----------------------------
  function initSearch() {
    const header = $('[data-header]');
    const toggle = $('[data-search-toggle]');
    const drawer = $('[data-search-drawer]');
    const closeBtn = $('[data-search-close]');
    const input = $('[data-predictive-input]');
    const results = $('[data-predictive-results]');
    if (!header || !toggle || !drawer) return;

    function open() { drawer.hidden = false; header.classList.add('is-search-open'); toggle.setAttribute('aria-expanded', 'true'); setTimeout(function () { input && input.focus(); }, 50); }
    function close() { drawer.hidden = true; header.classList.remove('is-search-open'); toggle.setAttribute('aria-expanded', 'false'); }
    on(toggle, 'click', function () { drawer.hidden ? open() : close(); });
    on(closeBtn, 'click', close);

    on(doc, 'keydown', function (e) { if (e.key === 'Escape' && !drawer.hidden) close(); });
    on(doc, 'click', function (e) { if (!drawer.hidden && !drawer.contains(e.target) && e.target !== toggle && !toggle.contains(e.target)) close(); });

    if (input && results) {
      const lookup = debounce(function () {
        const q = input.value.trim();
        if (q.length < 2) { results.innerHTML = ''; return; }
        const url = (theme.searchUrl || '/search') + '/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6';
        fetch(url, { headers: { 'Accept': 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
          .then(function (data) {
            const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
            if (products.length === 0) { results.innerHTML = '<p class="search-drawer__empty">No matches.</p>'; return; }
            const items = products.map(function (p) {
              const imgSrc = (p.featured_image && (p.featured_image.url || p.featured_image)) || p.image || '';
              const img = imgSrc ? '<img src="' + escapeHtml(imgSrc) + '" alt="" loading="lazy">' : '';
              const priceCents = p.price ? Math.round(parseFloat(p.price) * 100) : null;
              const priceHtml = priceCents != null ? '<small>' + escapeHtml(formatMoney(priceCents)) + '</small>' : '';
              return '<li><a href="' + escapeHtml(p.url || '#') + '">' + img + '<span><strong>' + escapeHtml(p.title || '') + '</strong>' + (priceHtml ? '<br>' + priceHtml : '') + '</span></a></li>';
            }).join('');
            results.innerHTML = '<ul>' + items + '</ul>';
          })
          .catch(function () { results.innerHTML = ''; });
      }, 220);
      on(input, 'input', lookup);
    }
  }

  // ---------------------------- locale menu ----------------------------
  function initLocaleMenu() {
    const wrap = $('[data-locale-switcher]');
    if (!wrap) return;
    const toggle = $('[data-locale-toggle]', wrap);
    const menu = $('[data-locale-menu]', wrap);
    on(toggle, 'click', function () {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      menu.hidden = expanded;
    });
    on(doc, 'click', function (e) {
      if (!wrap.contains(e.target)) { toggle.setAttribute('aria-expanded', 'false'); menu.hidden = true; }
    });
  }

  // ---------------------------- wishlist ----------------------------
  const WISHLIST_KEY = 'miaamor.wishlist.v2';
  const WISHLIST_LEGACY_KEY = 'miaamor.wishlist.v1';

  function wishlistIndexOf(list, handle) {
    for (let i = 0; i < list.length; i++) {
      if ((list[i].handle || list[i]) === handle) return i;
    }
    return -1;
  }

  function readWishlist() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      if (raw) return JSON.parse(raw);
      // Migrate from v1 (plain handle strings) → v2 (objects)
      const legacy = localStorage.getItem(WISHLIST_LEGACY_KEY);
      if (legacy) {
        const migrated = JSON.parse(legacy).map(function (item) {
          return typeof item === 'string' ? { handle: item } : item;
        });
        try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(migrated)); } catch (e) {}
        return migrated;
      }
      return [];
    } catch (e) { return []; }
  }

  function writeWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) {}
    updateWishlistUI();
    renderWishlistDrawer();
  }

  function updateWishlistUI() {
    const list = readWishlist();
    const counter = $('[data-wishlist-count]');
    if (counter) {
      if (list.length > 0) { counter.textContent = String(list.length); counter.hidden = false; }
      else { counter.hidden = true; }
    }
    $$('[data-wishlist-toggle]').forEach(function (btn) {
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      btn.setAttribute('aria-pressed', wishlistIndexOf(list, handle) > -1 ? 'true' : 'false');
    });
  }

  function renderWishlistDrawer() {
    const container = $('[data-wishlist-items]');
    const emptyState = $('[data-wishlist-empty]');
    if (!container) return;
    const list = readWishlist();
    if (list.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      return;
    }
    if (emptyState) emptyState.hidden = true;
    container.innerHTML = list.map(function (item) {
      const handle = item.handle || '';
      const title = item.title || handle;
      const url = item.url || ('/products/' + handle);
      const img = item.image || '';
      const price = item.price || '';
      return '<li class="wishlist-drawer__item">' +
        (img ? '<a href="' + escapeHtml(url) + '" class="wishlist-drawer__image-link"><img src="' + escapeHtml(img) + '" alt="' + escapeHtml(title) + '" width="72" height="72" loading="lazy" class="wishlist-drawer__image"></a>' : '') +
        '<div class="wishlist-drawer__info">' +
          '<a href="' + escapeHtml(url) + '" class="wishlist-drawer__name">' + escapeHtml(title) + '</a>' +
          (price ? '<span class="wishlist-drawer__price">' + escapeHtml(price) + '</span>' : '') +
        '</div>' +
        '<button type="button" class="wishlist-drawer__remove" data-wishlist-remove="' + escapeHtml(handle) + '" aria-label="Remove from wishlist">' +
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
      '</li>';
    }).join('');
  }

  function openWishlistDrawer() {
    const drawer = $('[data-wishlist-drawer]');
    if (!drawer) return;
    renderWishlistDrawer();
    drawer.setAttribute('aria-hidden', 'false');
    doc.body.classList.add('wishlist-open');
    const openBtn = $('[data-wishlist-open]');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
  }

  function closeWishlistDrawer() {
    const drawer = $('[data-wishlist-drawer]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    doc.body.classList.remove('wishlist-open');
    const openBtn = $('[data-wishlist-open]');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
  }

  function initWishlist() {
    // Portal to body — backdrop-filter on sticky header creates a containing block for
    // position:fixed children in Safari, constraining the drawer to the header's height.
    // Moving it to body gives it a proper full-viewport containing block (same fix as mobile menu).
    var wlDrawer = $('[data-wishlist-drawer]');
    if (wlDrawer && wlDrawer.parentElement !== doc.body) doc.body.appendChild(wlDrawer);

    // Toggle save/unsave on product cards and product pages
    on(doc, 'click', function (e) {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      e.preventDefault();
      const list = readWishlist();
      const idx = wishlistIndexOf(list, handle);
      if (idx > -1) {
        list.splice(idx, 1);
      } else {
        list.push({
          handle: handle,
          title: btn.getAttribute('data-product-title') || handle,
          url: btn.getAttribute('data-product-url') || ('/products/' + handle),
          image: btn.getAttribute('data-product-image') || '',
          price: btn.getAttribute('data-product-price') || ''
        });
      }
      writeWishlist(list);
    });

    // Open drawer via header button
    on(doc, 'click', function (e) {
      if (e.target.closest('[data-wishlist-open]')) openWishlistDrawer();
    });

    // Close drawer via backdrop or close button
    on(doc, 'click', function (e) {
      if (e.target.closest('[data-wishlist-close]')) closeWishlistDrawer();
    });

    // Remove item from drawer list
    on(doc, 'click', function (e) {
      const btn = e.target.closest('[data-wishlist-remove]');
      if (!btn) return;
      const handle = btn.getAttribute('data-wishlist-remove');
      if (!handle) return;
      const list = readWishlist();
      const idx = wishlistIndexOf(list, handle);
      if (idx > -1) list.splice(idx, 1);
      writeWishlist(list);
    });

    // Escape key closes drawer
    on(doc, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      const drawer = $('[data-wishlist-drawer]');
      if (drawer && drawer.getAttribute('aria-hidden') === 'false') closeWishlistDrawer();
    });

    updateWishlistUI();
  }

  // ---------------------------- quantity inputs ----------------------------
  function initQuantitySteppers() {
    on(doc, 'click', function (e) {
      const btn = e.target.closest('[data-qty-step]');
      if (!btn) return;
      const step = parseInt(btn.getAttribute('data-qty-step'), 10) || 1;
      const wrap = btn.closest('.quantity-input');
      if (!wrap) return;
      const input = wrap.querySelector('input[type="number"]');
      if (!input) return;
      const min = parseInt(input.getAttribute('min'), 10);
      const next = (parseInt(input.value, 10) || 0) + step;
      input.value = isNaN(min) ? Math.max(0, next) : Math.max(min, next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // ---------------------------- product page ----------------------------
  function initProductSection() {
    const section = $('[data-product-section]');
    if (!section) return;
    const dataNode = $('[data-product-json]', section);
    if (!dataNode) return;
    let productData;
    try { productData = JSON.parse(dataNode.textContent); } catch (e) { return; }

    const variantInputs = $$('[data-option-input]', section);
    const hiddenIdInput = $('[data-variant-id]', section);
    const atc = $('[data-atc]', section);
    const atcLabel = $('[data-atc-label]', section);
    const priceTarget = $('[data-price-target]', section);
    const mediaMain = $('[data-media-main]', section);

    function selectedOptions() {
      const map = {};
      variantInputs.forEach(function (i) { if (i.checked) map[i.name.replace(/^options\[(.+)\]$/, '$1')] = i.value; });
      return productData.options.map(function (name) { return map[name]; });
    }
    function findVariant() {
      const opts = selectedOptions();
      return productData.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
    }
    function applyVariant(v) {
      if (!v) {
        if (atc) { atc.disabled = true; atcLabel && (atcLabel.textContent = 'Unavailable'); }
        return;
      }
      hiddenIdInput && (hiddenIdInput.value = String(v.id));
      if (atc) {
        atc.disabled = !v.available;
        if (atcLabel) atcLabel.textContent = v.available ? (atcLabel.dataset.add || atcLabel.textContent) : 'Sold Out';
      }
      if (priceTarget) {
        let html = '<span class="price__current">' + formatMoney(v.price) + '</span>';
        if (v.compare_at_price && v.compare_at_price > v.price) html += '<span class="price__compare">' + formatMoney(v.compare_at_price) + '</span>';
        priceTarget.innerHTML = '<span class="price">' + html + '</span>';
      }
      if (v.featured_image && mediaMain) {
        const img = mediaMain.querySelector('img');
        if (img) { img.src = v.featured_image.src || v.featured_image; img.removeAttribute('srcset'); }
      }
      const url = new URL(win.location.href);
      url.searchParams.set('variant', String(v.id));
      win.history.replaceState({}, '', url);
    }

    variantInputs.forEach(function (inp) { on(inp, 'change', function () { applyVariant(findVariant()); }); });

    $$('[data-media-thumb]', section).forEach(function (btn) {
      on(btn, 'click', function () {
        $$('[data-media-thumb]', section).forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        const src = btn.getAttribute('data-src');
        const img = mediaMain && mediaMain.querySelector('img');
        if (img && src) { img.src = src; img.removeAttribute('srcset'); }
      });
    });
  }

  // ---------------------------- AJAX add to cart from cards ----------------------------
  function initQuickAdd() {
    on(doc, 'submit', function (e) {
      const form = e.target.closest('[data-quick-add]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      btn && btn.setAttribute('disabled', 'true');
      const fd = new FormData(form);
      const body = {};
      fd.forEach(function (v, k) { body[k] = v; });
      body.quantity = parseInt(body.quantity, 10) || 1;
      body.id = parseInt(body.id, 10);
      fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify(body) })
        .then(function () { return fetchJSON('/cart.js'); })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          flashAtc(btn, 'Added ✓');
        })
        .catch(function () { flashAtc(btn, 'Error'); })
        .finally(function () { btn && btn.removeAttribute('disabled'); });
    });
  }
  function updateCartCount(count) {
    const el = $('[data-cart-count]');
    if (!el) return;
    el.textContent = String(count);
    el.hidden = count <= 0;
  }
  function flashAtc(btn, text) {
    if (!btn) return;
    const span = btn.querySelector('span');
    if (!span) return;
    const orig = span.textContent;
    span.textContent = text;
    setTimeout(function () { span.textContent = orig; }, 1600);
  }

  // ---------------------------- cart page ----------------------------
  function initCart() {
    const cart = $('[data-cart-section]');
    if (!cart) return;

    const debouncedUpdate = debounce(function (line, qty) {
      fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ line: line, quantity: qty }) })
        .then(function (data) { renderCartSummary(data); })
        .catch(function () { win.location.reload(); });
    }, 350);

    on(cart, 'change', function (e) {
      const input = e.target.closest('[data-line-qty-input]');
      if (!input) return;
      const line = parseInt(input.getAttribute('data-line'), 10);
      const qty = parseInt(input.value, 10);
      if (isNaN(line) || isNaN(qty)) return;
      debouncedUpdate(line, qty);
    });

    on(cart, 'click', function (e) {
      const remove = e.target.closest('[data-cart-remove]');
      if (!remove) return;
      e.preventDefault();
      const line = parseInt(remove.getAttribute('data-line'), 10);
      fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ line: line, quantity: 0 }) })
        .then(function () { win.location.reload(); });
    });
  }

  function renderCartSummary(data) {
    updateCartCount(data.item_count);
    const subtotal = $('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(data.total_price);
    updateFreeShippingBar(data.total_price);
    data.items.forEach(function (item, idx) {
      const row = doc.querySelector('[data-cart-item][data-line="' + (idx + 1) + '"]');
      if (!row) return;
      const total = row.querySelector('.cart-item__total');
      if (total) total.textContent = formatMoney(item.final_line_price);
      const qty = row.querySelector('[data-line-qty-input]');
      if (qty && parseInt(qty.value, 10) !== item.quantity) qty.value = item.quantity;
    });
  }

  function updateFreeShippingBar(totalCents) {
    const bar = $('[data-free-shipping-bar]');
    if (!bar) return;
    const threshold = (theme.freeShippingThreshold || 150) * 100;
    const fill = bar.querySelector('.free-shipping-bar__fill');
    const text = bar.querySelector('p');
    const pct = Math.min(100, (totalCents / threshold) * 100);
    if (fill) fill.style.width = pct + '%';
    if (text) {
      if (totalCents >= threshold) text.textContent = 'Free shipping unlocked 🎉';
      else text.innerHTML = 'Add <strong>' + formatMoney(threshold - totalCents) + '</strong> more for free shipping';
    }
  }

  // ---------------------------- async product recommendations ----------------------------
  function initRecommendations() {
    const sec = $('[data-product-recommendations]');
    if (!sec) return;
    if (sec.querySelector('.product-grid')) return; // already rendered
    const productId = sec.getAttribute('data-product-id');
    const sectionId = sec.getAttribute('data-section-id');
    const limit = sec.getAttribute('data-limit') || 4;
    const intent = sec.getAttribute('data-intent') || 'related';
    if (!productId) return;
    const url = '/recommendations/products?section_id=' + encodeURIComponent(sectionId) + '&product_id=' + productId + '&limit=' + limit + '&intent=' + intent;
    fetch(url).then(function (r) { return r.text(); }).then(function (html) {
      const wrap = doc.createElement('div');
      wrap.innerHTML = html;
      const inner = wrap.querySelector('.product-recommendations');
      if (inner && inner.children.length > 0) sec.replaceWith(inner);
    }).catch(function () {});
  }

  // ---------------------------- cookie consent ----------------------------
  function initCookieBanner() {
    const banner = $('[data-cookie-banner]');
    if (!banner) return;
    const KEY = 'miaamor.consent.v1';

    function shopifyConsentResolved() {
      try {
        const cp = win.Shopify && win.Shopify.customerPrivacy;
        if (cp && typeof cp.userCanBeTracked === 'function') {
          // If Shopify already has a decision recorded (region + answer), don't show our banner.
          if (typeof cp.shouldShowGDPRBanner === 'function' && cp.shouldShowGDPRBanner() === false) return true;
        }
      } catch (e) {}
      return false;
    }

    const saved = (function () { try { return localStorage.getItem(KEY); } catch (e) { return null; } })();
    if (!saved && !shopifyConsentResolved()) banner.hidden = false;

    function persist(value) {
      try { localStorage.setItem(KEY, value); } catch (e) {}
      banner.hidden = true;
      try {
        const cp = win.Shopify && win.Shopify.customerPrivacy;
        if (cp && typeof cp.setTrackingConsent === 'function') {
          cp.setTrackingConsent(value === 'accept', function () {});
        }
      } catch (e) {}
    }

    on($('[data-cookie-accept]', banner), 'click', function () { persist('accept'); });
    on($('[data-cookie-decline]', banner), 'click', function () { persist('decline'); });
  }

  // ---------------------------- bootstrap ----------------------------
  function init() {
    safeRun('mobile-nav', initMobileNav);
    safeRun('search', initSearch);
    safeRun('locale-menu', initLocaleMenu);
    safeRun('wishlist', initWishlist);
    safeRun('quantity', initQuantitySteppers);
    safeRun('product', initProductSection);
    safeRun('quick-add', initQuickAdd);
    safeRun('cart', initCart);
    safeRun('recommendations', initRecommendations);
    safeRun('cookie-banner', initCookieBanner);
  }

  if (doc.readyState === 'loading') on(doc, 'DOMContentLoaded', init); else init();
})();
